import { Link, useLocation, useMatches } from '@remix-run/react';
import type { URLInfo } from '@archipro-website/top-navigation';
import { useTracker } from '@archipro-website/tracker';
import type { Category, CategorySelectorProps } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import { pxArrayToRem } from '@archipro-design/aria';
import { CategorySelector, pxToRem } from '@archipro-design/aria';
import type { FilterStatesKey } from '../../type/filter-state';
import { ArrowLeftSLine } from '@archipro-design/icons';
import * as S from './FilterCategorySelector.style';
import {
    LEGACY_PATH_PARAM_KEYS,
    generateURL,
    getString,
    parseURL,
} from '@archipro-website/top-navigation';
import type { APHandle, FeatureFlags } from '~/modules/root';
import { useRootData } from '~/modules/root';
import { getPathFilterKeysByCategory } from '../../util/filter-url-helpers';
import { useUser } from '~/modules/user';
import { convertToProductSuppliersUrl } from '~/modules/url';

export interface CategoryFilter {
    kind: 'CategorySelector';
    filterKey: FilterStatesKey;
    subFilterKey?: string;
    props: CategorySelectorProps;
}
export type CategoryFilterState = Pick<
    CategorySelectorProps,
    'category' | 'selectedCategory'
> &
    Pick<CategoryFilter, 'kind'> & {
        isRoot?: boolean;
    };

interface FilterCheckboxProps {
    filter: CategoryFilter;
    state: CategoryFilterState;
    updateFilter: (
        filterKey: FilterStatesKey,
        state: Partial<CategoryFilterState>
    ) => void;
    disabled?: boolean;
    hideCategoryBackButton?: boolean;
}

const FilterCategorySelector = ({
    filter,
    state,
    updateFilter,
    disabled,
    hideCategoryBackButton,
}: FilterCheckboxProps) => {
    const { props, filterKey } = filter;
    const { css } = useStyles({ isRoot: state.isRoot });
    const location = useLocation();
    const tracker = useTracker();
    const {
        device: { isBot, type },
    } = useRootData();

    const user = useUser();
    const matches = useMatches();
    const isCategoryPage = !!matches.find(
        (m) => (m.handle as APHandle)?.isCategoryPage
    );
    const category = state.category ?? props.category;
    const urlInfo = parseURL(location.pathname + location.search);
    const displayBackButton =
        type === 'desktop' && !state.isRoot && !hideCategoryBackButton;

    return (
        <CategorySelector
            {...props}
            className={css(S.CategorySelector)}
            category={category}
            onChange={(e, data) => {
                const category = data?.category;
                if (type !== 'desktop') {
                    e.preventDefault();
                    updateFilter(filterKey, {
                        kind: 'CategorySelector',
                        selectedCategory: category ?? null,
                        isRoot: !category || state.category.id === category?.id,
                    });
                    return;
                }

                // desktop only
                if (category?.id && data?.source === 'item') {
                    tracker.log('LeftNavCategoryClick', {
                        url: new URL(window.location.href),
                        targetTracker: ['archiproTracker', 'archiproTrackerV2'],
                        data: {
                            ExtraData: category.id,
                        },
                    });
                }
            }}
            defaultCategoryHeaderProps={(cat) => {
                return Object.assign(
                    type === 'desktop'
                        ? {
                              as: Link,
                              to: constructCategoryLink(
                                  cat,
                                  urlInfo,
                                  isBot,
                                  isCategoryPage,
                                  false,
                                  user.FeatureFlags
                              ),
                          }
                        : {},
                    props.defaultCategoryHeaderProps
                );
            }}
            defaultCategoryItemProps={(cat) => {
                return Object.assign(
                    type === 'desktop'
                        ? {
                              as: Link,
                              to: constructCategoryLink(
                                  cat,
                                  urlInfo,
                                  isBot,
                                  isCategoryPage,
                                  true,
                                  user.FeatureFlags
                              ),
                          }
                        : {},
                    props.defaultCategoryItemProps
                );
            }}
            selectedCategory={state.selectedCategory}
            disabled={disabled}
            backButton={
                displayBackButton && {
                    as: Link,
                    preventScrollReset: true,
                    to: constructCategoryLink(
                        state?.selectedCategory?.parent,
                        urlInfo,
                        isBot,
                        isCategoryPage,
                        false,
                        user.FeatureFlags
                    ),
                }
            }
        />
    );
};

export default FilterCategorySelector;

export const CATEGORY_SELECTOR_MOBILE_ARGS: Partial<CategorySelectorProps> = {
    showSelectedChain: true,
    categoryHeaderIcon: <ArrowLeftSLine />,
    defaultCategoryHeaderProps: {
        size: 16,
        variables: {
            buttonPadding: pxArrayToRem([8, 8, 8.5, 8]),
            iconPadding: 0,
            primaryNoBKGButtonBackgroundColorSelected: 'transparent',
            primaryNoBKGButtonBackgroundColorHover: 'transparent',
        },
        variant: 'text',
    },
    defaultCategoryItemProps: {
        size: 16,
        variables: {
            buttonPadding: pxArrayToRem([8, 8, 8.5, 8]),
            iconPadding: 0,
            primaryNoBKGButtonBackgroundColorSelected: 'transparent',
            primaryNoBKGButtonBackgroundColorHover: 'transparent',
        },
        design: {
            marginLeft: pxToRem(32),
        },
        variant: 'text',
    },
    variables: {
        headerCategoryGap: '0',
        headerCategoryWeight: 400,
        selectedCategoryWeight: 500,
        selectedHeaderCategoryWeight: 500,
        selectedCategoryIconOpacity: 0,
        headerCategoryItemGap: pxToRem(2),
        categoryIconSize: pxToRem(16),
    },
};

const constructCategoryLink = (
    cat: Category | undefined,
    urlInfo: URLInfo,
    isBot: boolean,
    isCategoryPage: boolean,
    isChildCategory: boolean,
    featureSet: FeatureFlags
): string => {
    const { basePath, params: originalParam, isSuppliersEndpoint } = urlInfo;
    const params = { ...originalParam };

    // If no parent category, we are going to the root search page
    if (!cat || cat.root) {
        delete params['categoryLink'];
        delete params['category'];
        return generateURL(
            isSuppliersEndpoint
                ? convertToProductSuppliersUrl(basePath)
                : basePath,
            params
        );
    }

    // Don't add query params to bot links, they will waste our SEO crawl budget
    // Add filter params for users to keep their filter state
    if (isBot) {
        return isCategoryPage
            ? cat.id
            : generateURL(basePath, {
                  categoryLink: cat.id,
                  search: getString(params, 'search') ?? '',
              });
    }

    if (isCategoryPage) {
        const mergedBasePath = isSuppliersEndpoint
            ? convertToProductSuppliersUrl(cat.id)
            : cat.id;

        if (isChildCategory) {
            // update category and keep search params
            return generateURL(
                mergedBasePath,
                params,
                false,
                LEGACY_PATH_PARAM_KEYS,
                getPathFilterKeysByCategory(featureSet, cat, true)
            );
        } else {
            // remove all filters params
            return generateURL(mergedBasePath);
        }
    } else {
        const mergedBasePath = isSuppliersEndpoint
            ? convertToProductSuppliersUrl(basePath)
            : basePath;

        if (isChildCategory) {
            // update category and keep search params
            params['categoryLink'] = cat.id;
            return generateURL(mergedBasePath, params);
        } else {
            // remove all filters params
            return generateURL(mergedBasePath, {
                categoryLink: cat.id,
                category: params['category'] ?? '',
                search: getString(params, 'search') ?? '',
            });
        }
    }
};
